import * as d3 from 'd3';

export const scaled = (fullsize, width) => {
  return fullsize * width / 954
};

export const intermediateValue = (value, extent) => {
  return ((extent[1] - extent[0]) * value) + extent[0];
};

export const adjustProjection = (projection, path, data, options={}) => {
  projection.scale(1).translate([0, 0]);
  const b = path.bounds(data);
  if (options.size == null && options.scale == null) {
    throw new Error('You need to supply a size or scale')
  }
  let s = options.scale != null ? options.scale : 1 / Math.max((b[1][0] - b[0][0]) / options.size[0], (b[1][1] - b[0][1]) / options.size[1]);
  let t;
  if (options.size != null) {
    if (options.origin == null) {
      options.origin = [0, 0];
    }
    t = [(options.size[0] - s * (b[1][0] + b[0][0])) / 2 + options.origin[0], (options.size[1] - s * (b[1][1] + b[0][1])) / 2 + options.origin[1]];
  } else if (options.center != null) {
    t = [options.center[0] - (s * (b[1][0] + b[0][0]) / 2), options.center[1] - (s * (b[1][1] + b[0][1]) / 2)];
  } else {
    throw new Error('You need to supply a size or center');
  }
  projection.scale(s).translate(t);
  const transformedBounds = [[b[0][0] * s + t[0], b[0][1] * s + t[1]], [b[1][0] * s + t[0], b[1][1] * s + t[1]]];
  return [s, t, transformedBounds];
};

export const getLastDate = (data, nonDateColumns, dateFormat) => {
  const dates = [];
  for (let property in data[0]) {
    if (!nonDateColumns.includes(property)) {
      dates.push(d3.timeParse(dateFormat)(property))
    }
  }

  return dates[dates.length - 1]
};

export const processDateSeriesCsvData = (data, nonDateColumns, dateFormat = '%Y-%m-%d') => {
  const retRows = [];
  data.forEach(row => {
    const values = [];
    for (let property in row) {
      if (!nonDateColumns.includes(property)) {
        let value;
        if (row[property] === '') {
          value = 0;
        } else {
          value = parseFloat(row[property]);
        }
        values.push(value);
      }
    }
    const retRow = {};
    retRow.values = values;
    nonDateColumns.forEach(column => {
      retRow[column] = row[column];
    });
    retRows.push(retRow);
  });

  const dates = [];
  for (let property in data[0]) {
    if (!nonDateColumns.includes(property)) {
      dates.push(d3.timeParse(dateFormat)(property));
    }
  }

  return [retRows, dates];
};

export const allValuesFromDateSeriesData = (data) => {
  const values = [];
  data.forEach(function(row) {
    values.push.apply(values, row.values);
  });
  return values;
};

export const selectOrAppend = (parent, selector) => {
  let sel = parent.select(selector);
  if (sel.empty()) {
    let tag, className, id;
    if (selector.includes('.')) {
      [tag, className] = selector.split('.');
    } else if (selector.includes('#')) {
      [tag, id] = selector.split('#');
    } else {
      tag = selector;
    }
    sel = parent.append(tag);
    if (className != null) {
      sel.attr('class', className);
    } else if (id != null) {
      sel.attr('id', id);
    }
  }
  return sel;
};

export const selectOrAppendDefs = (svg) => {
  return selectOrAppend(svg, 'defs');
};

export const createDropShadowFilter = (svg, id, offsetX, offsetY, blur, opacity = 0.5, expand = 10) => {
  const defs = selectOrAppendDefs(svg);
  const filter = defs.append('filter')
    .attr('id', id);
  if (expand !== 10) {
    filter.attr('x', `-${expand}%`)
      .attr('y', `-${expand}%`)
      .attr('width', `${100 + (expand * 2)}%`)
      .attr('height', `${100 + (expand * 2)}%`);
  }
  filter.append('feGaussianBlur')
    .attr('in', 'SourceAlpha')
    .attr('stdDeviation', blur);
  filter.append('feOffset')
    .attr('dx', offsetX)
    .attr('dy', offsetY)
    .attr('result', 'offsetBlur');
  filter.append('feComponentTransfer')
    .append('feFuncA')
    .attr('type', 'linear')
    .attr('slope', opacity);
  const feMerge = filter.append('feMerge');
  feMerge.append('feMergeNode');
  feMerge.append('feMergeNode')
    .attr('in', 'SourceGraphic');
  return filter;
};

export const createBlurFilter = (svg, id, blur, expand = 10) => {
  const defs = selectOrAppendDefs(svg);
  const filter = defs.append('filter')
    .attr('id', id);
  if (expand !== 10) {
    filter.attr('x', `-${expand}%`)
      .attr('y', `-${expand}%`)
      .attr('width', `${100 + (expand * 2)}%`)
      .attr('height', `${100 + (expand * 2)}%`);
  }
  filter.append('feGaussianBlur')
    .attr('in', 'SourceGraphic')
    .attr('stdDeviation', blur);
  return filter;
};

export const buildCacheParam = () => {
  return `${d3.timeFormat('%Y%m%d')(new Date())}.1`;
};

export const formatDate = (date) => {
  if (date) {
    return d3.timeFormat('%-d/%-m/%Y')(date)
  } else {
    return '';
  }
};

export const csvToMap = (data, idColumnName, getValueFunc = undefined) => {
  const map = new Map();
  data.forEach(datum => {
    const object = {};
    for (const key of Object.keys(datum)) {
      if (key !== idColumnName) {
        let value;
        if (getValueFunc != null) {
          value = getValueFunc(key, datum[key]);
        } else {
          value = datum[key];
        }
        object[key] = value;
      }
    }
    map.set(datum[idColumnName], object);
  });
  return map;
};
